import React, { useState, useEffect, useCallback } from "react";
import ProfileNav from "../../components/ProfileNav";
import { fetchGlobalSubscriptions, fetchNetworkSummary } from "../../services/organisation";

const SubscriptionPage = () => {
  const [activeTab, setActiveTab] = useState('all-subs');
  const [globalSubs, setGlobalSubs] = useState({ subscriptions: [], totalCount: 0, totalPages: 0 });
  const [networkSummary, setNetworkSummary] = useState({ items: [], totalCount: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'all-subs') {
        const res = await fetchGlobalSubscriptions(page, pageSize);
        setGlobalSubs(res);
      } else {
        const res = await fetchNetworkSummary(page, pageSize);
        setNetworkSummary(res);
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'bg-[#dcfce7] text-[#166534]';
      case 'PAUSED': return 'bg-[#fef9c3] text-[#854d0e]';
      case 'CANCELLED': return 'bg-[#fee2e2] text-[#991b1b]';
      default: return 'bg-[#f1f5f9] text-[#64748b]';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderPagination = (totalPages) => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#e2e8f0]">
        <p className="text-sm text-[#64748b]">
          Page {page} of {totalPages}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="px-4 py-2 text-sm border border-[#e2e8f0] rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            Previous
          </button>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
            className="px-4 py-2 text-sm border border-[#e2e8f0] rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-[#f8fafc] font-sans text-[#334155]">
      <div className="h-full flex flex-col gap-4 p-5 max-w-[1200px] mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#334155] mt-0">Subscription Network Overview</h1>
            <p className="text-[#64748b] mt-2 mb-8">Monitor and manage recurring billing cycles across the network.</p>
          </div>
          <div>
            <ProfileNav />
          </div>
        </div>

        <div className="bg-white border border-[#e2e8f0] rounded-lg shadow-sm overflow-hidden flex-grow flex flex-col">
          <div className="flex border-b border-[#e2e8f0]">
            <button
              className={`px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'all-subs'
                ? 'text-[#f58220] border-b-2 border-[#f58220]'
                : 'text-[#64748b] hover:text-[#334155]'
                }`}
              onClick={() => handleTabChange('all-subs')}
            >
              Global Subscriptions
            </button>
            <button
              className={`px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'merchant-plans'
                ? 'text-[#f58220] border-b-2 border-[#f58220]'
                : 'text-[#64748b] hover:text-[#334155]'
                }`}
              onClick={() => handleTabChange('merchant-plans')}
            >
              Merchant Plan Configurations
            </button>
          </div>

          <div className="overflow-x-auto w-full flex-grow p-6 flex flex-col">
            {loading && (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f58220]"></div>
              </div>
            )}

            {!loading && activeTab === 'all-subs' && (
              <div id="all-subs" className="animate-fade-in flex-grow">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Customer Email</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Merchant Name</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Plan Assigned</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Amount</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Next Cycle</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalSubs.subscriptions.length > 0 ? (
                      globalSubs.subscriptions.map((sub) => (
                        <tr key={sub.id} className="border-b border-[#e2e8f0] hover:bg-slate-50 transition-colors">
                          <td className="p-4 text-sm text-[#334155]">{sub.customerEmail}</td>
                          <td className="p-4 text-sm text-[#334155]">
                            <span className="font-semibold text-[#0f172a]">{sub.merchantName}</span>
                          </td>
                          <td className="p-4 text-sm text-[#334155]">{sub.planName}</td>
                          <td className="p-4 text-sm text-[#334155]">{sub.currency} {sub.amount?.toLocaleString()}</td>
                          <td className="p-4 text-sm text-[#334155]">{formatDate(sub.nextBillingDate)}</td>
                          <td className="p-4 text-sm">
                            <span className={`${getStatusColor(sub.status)} px-3 py-1 rounded-full text-xs font-semibold`}>
                              {sub.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-[#64748b]">No global subscriptions found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {renderPagination(globalSubs.totalPages)}
              </div>
            )}

            {!loading && activeTab === 'merchant-plans' && (
              <div id="merchant-plans" className="animate-fade-in flex-grow">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Merchant Owner</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Plan Name</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Frequency</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Price</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Max Payments</th>
                      <th className="bg-[#f1f5f9] p-4 text-xs uppercase font-semibold text-[#64748b] border-b border-[#e2e8f0]">Total Subs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {networkSummary.items.length > 0 ? (
                      networkSummary.items.map((item, idx) => (
                        <tr key={idx} className="border-b border-[#e2e8f0] hover:bg-slate-50 transition-colors">
                          <td className="p-4 text-sm text-[#334155]">
                            <strong>{item.merchantOwner}</strong>
                          </td>
                          <td className="p-4 text-sm text-[#334155]">{item.planName}</td>
                          <td className="p-4 text-sm text-[#334155]">{item.frequency}</td>
                          <td className="p-4 text-sm text-[#334155]">{item.price}</td>
                          <td className="p-4 text-sm text-[#334155]"><strong>{item.maxPayments}</strong></td>
                          <td className="p-4 text-sm text-[#334155]">{item.totalSubs?.toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-[#64748b]">No merchant plan configurations found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {renderPagination(networkSummary.totalPages)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;

